import { lazy } from 'react';
import { getAndPresavePhoneProps } from 'app/props/presaved-props';
import { errorExistsIn } from 'utils/error-utils';
import { ACCESS_BLOCKED_REASONS, LOGIN_TYPES } from 'consts/login-constants';

import KeycloakFallback from 'app/app-fallback';

import {
  KcContext,
  KcErrorContext,
  KcRegisterContext,
  BlockedErrors,
  RegistrationErrors,
  ResetPasswordErrors,
  KcResetPasswordContext,
  PhoneAuthErrors,
  KcLoginContext,
} from 'types';

import {
  getSignInPropsFromKcContext,
} from 'app/props/kc-context-to-props';

const AccessBlockedPage = lazy(() => import('pages/access-blocked-page'));
const AccessBlockedFullyPage = lazy(() => import('pages/access-blocked-fully-page'));
const RegistrationStep2OtpPage = lazy(() => import('pages/registration-step-2-otp-page'));
const AuthSignInPage = lazy(() => import('pages/auth-sign-in-page'));
const DkboPage = lazy(() => import('pages/dkbo-page'));
const RecoverPasswordDisabledPage = lazy(() => import('pages/recover-password-disabled-page'));

export default function AppErrorFlow(kcContext: KcContext) {
  const error = kcContext.message?.summary;

  if (errorExistsIn(error, PhoneAuthErrors)) {
    return SignInError(kcContext as KcLoginContext);
  }

  if (errorExistsIn(error, RegistrationErrors)) {
    return RegistrationError(kcContext as KcRegisterContext);
  }

  if (errorExistsIn(error, ResetPasswordErrors)) {
    return ResetPasswordError(kcContext as KcResetPasswordContext);
  }

  if (errorExistsIn(error, BlockedErrors)) {
    return AccessBlockedError(kcContext as KcErrorContext);
  }

  return <KeycloakFallback />;
}

function AccessBlockedError(kcContext: KcErrorContext) {
  const error = kcContext.message?.summary;

  switch (error) {
    case BlockedErrors.BLOCKED_BY_BRUTEFORCE:
      return <AccessBlockedPage
        blockDurationSeconds={kcContext.bruteforce_block_time}
        blockReason={ACCESS_BLOCKED_REASONS.PASSWORD}
      />;

    case BlockedErrors.BLOCKED_SCRATCH_CARD:
      return <AccessBlockedPage
        blockDurationSeconds={kcContext.block_time}
        blockReason={ACCESS_BLOCKED_REASONS.SCRATCH}
      />;

    case BlockedErrors.BLOCKED_RECOVER_PASSWORD:
      return <AccessBlockedPage
        blockDurationSeconds={kcContext.block_time}
        blockReason={ACCESS_BLOCKED_REASONS.RECOVER}
      />;

    case BlockedErrors.BLOCKED_FULLY_BY_KYC:
    case BlockedErrors.BLOCKED_MANUALLY:
      return <AccessBlockedFullyPage />;

    case BlockedErrors.BLOCKED_FULLY_BY_DKO:
      return <DkboPage />;

    case BlockedErrors.BLOCKED_FULLY_BY_KYC_VIP:
      return <AccessBlockedFullyPage vip />;    
    
    default:
      throw new Error(`AccessBlockedError [${error}] not found.`);
  }
}

function SignInError(kcContext: KcLoginContext) {
  const error = kcContext.message?.summary;

  switch (error) {
    case PhoneAuthErrors.INVALID_GRANT_CONSENT:
      return <AuthSignInPage
        {...getSignInPropsFromKcContext(kcContext)}
        loginType={LOGIN_TYPES.BY_USERNAME}
        error={error}
      />;
      
    default:
      throw new Error(`SignInError [${error}] not found.`);
  }
}

function RegistrationError(kcContext: KcRegisterContext) {
  const error = kcContext.message?.summary;

  switch (error) {
    case RegistrationErrors.USER_ALREADY_REGISTERED:
      return <RegistrationStep2OtpPage
        {...getAndPresavePhoneProps(kcContext)}
        resendCodePosibleAfterSeconds={kcContext.sms_blocking_time}
        alreadyRegistered
      />;
      
    default:
      throw new Error(`RegistrationError [${error}] not found.`);
  }
}

function ResetPasswordError(kcContext: KcResetPasswordContext) {
  const error = kcContext.message?.summary;

  switch (error) {
    case ResetPasswordErrors.REMOTE_RECOVERY_DISABLED:
      return <RecoverPasswordDisabledPage />;
      
    default:
      throw new Error(`ResetPasswordError [${error}] not found.`);
  }
}
